const MESSAGE_URL_PATTERN = /\b(?:(?:https?:\/\/|www\.)[^\s<>"']+|(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}(?::\d{1,5})?(?:[/?#][^\s<>"']*)?)/gi;

function splitUrlTrailingPunctuation(rawUrl) {
  let url = String(rawUrl || '');
  let trailing = '';
  while (/[.,!?;:]$/.test(url)) {
    trailing = url.slice(-1) + trailing;
    url = url.slice(0, -1);
  }
  while (url.endsWith(')') && (url.match(/\(/g) || []).length < (url.match(/\)/g) || []).length) {
    trailing = ')' + trailing;
    url = url.slice(0, -1);
  }
  while (url.endsWith(']') && (url.match(/\[/g) || []).length < (url.match(/\]/g) || []).length) {
    trailing = ']' + trailing;
    url = url.slice(0, -1);
  }
  while (url.endsWith('}') && (url.match(/\{/g) || []).length < (url.match(/\}/g) || []).length) {
    trailing = '}' + trailing;
    url = url.slice(0, -1);
  }
  return { url, trailing };
}

function normalizedUrl(value) {
  const raw = String(value || '').trim();
  if (!raw) return null;
  try {
    return new URL(/^[a-z][a-z\d+.-]*:\/\//i.test(raw) ? raw : `https://${raw}`);
  } catch {
    return null;
  }
}

function unicodeLength(value) {
  return Array.from(String(value || '')).length;
}

function unicodeSlice(value, start, end) {
  return Array.from(String(value || '')).slice(start, end).join('');
}

function compactPathWithOneEllipsis(pathParts, maximumLength) {
  if (maximumLength <= 1) return '…';

  const plainPath = pathParts.join('/').replaceAll('…', '...');
  const preferredPrefixParts = pathParts.length >= 5
    ? pathParts.slice(0, 2)
    : (pathParts.length >= 3 ? pathParts.slice(0, 1) : []);
  const preferredPrefix = preferredPrefixParts.length > 0
    ? `${preferredPrefixParts.join('/').replaceAll('…', '...')}/`
    : '';
  const preferredSuffix = preferredPrefixParts.length > 0
    ? `/${String(pathParts[pathParts.length - 1]).replaceAll('…', '...')}`
    : '';
  const preferred = `${preferredPrefix}…${preferredSuffix}`;
  if (preferredPrefixParts.length > 0 && unicodeLength(preferred) <= maximumLength) return preferred;

  const contextLength = maximumLength - 1;
  const headLength = Math.ceil(contextLength * 0.6);
  const tailLength = Math.max(0, contextLength - headLength);
  let head = unicodeSlice(plainPath, 0, headLength).replace(/\/+$/, '');
  let tail = tailLength > 0 ? unicodeSlice(plainPath, -tailLength).replace(/^\/+/, '') : '';

  if (!head && !tail) return '…';
  if (!head) return `…${tail}`;
  if (!tail) return `${head}…`;
  return `${head}…${tail}`;
}

/**
 * Match the native iOS display rule without changing the navigated URL:
 * preserve the complete host, omit query/fragment noise, and compact paths.
 */
export function compactDisplayUrl(value, maximumLength = 64) {
  const safeMaximumLength = Number.isFinite(maximumLength)
    ? Math.max(1, Math.floor(maximumLength))
    : 64;
  const parsed = normalizedUrl(value);
  if (!parsed) {
    const fallback = String(value || '').trim()
      .replace(/^https?:\/\//i, '')
      .replace(/^www\./i, '')
      .split(/[?#]/, 1)[0];
    // With no parseable host boundary, shortening could cut the domain.
    return fallback;
  }

  let host = parsed.hostname.replace(/^www\./i, '');
  const isDefaultPort = (parsed.protocol === 'https:' && parsed.port === '443')
    || (parsed.protocol === 'http:' && parsed.port === '80');
  if (parsed.port && !isDefaultPort) host += `:${parsed.port}`;

  let path = parsed.pathname;
  try { path = decodeURIComponent(path); } catch { /* Keep the encoded path. */ }
  if (path === '/') path = '';
  while (path.length > 1 && path.endsWith('/')) path = path.slice(0, -1);
  const pathParts = path.split('/').filter(Boolean);
  if (pathParts.length === 0) return host;

  const full = `${host}/${pathParts.join('/')}`;
  if (unicodeLength(full) <= safeMaximumLength) return full;

  // This is a soft limit: the exact host is never shortened. When the host
  // already consumes the budget, retain the final path segment as useful
  // context instead of reducing the entire path to a bare marker.
  const availablePathLength = safeMaximumLength - unicodeLength(host) - 1;
  if (availablePathLength <= 1) {
    const finalSegment = String(pathParts[pathParts.length - 1]).replaceAll('…', '...');
    return `${host}/…/${finalSegment}`;
  }

  return `${host}/${compactPathWithOneEllipsis(pathParts, availablePathLength)}`;
}

export function tokenizeMessageUrls(value) {
  const links = [];
  const source = String(value ?? '');
  const text = source.replace(MESSAGE_URL_PATTERN, (rawUrl, offset) => {
    if (source[offset - 1] === '@') return rawUrl;
    const { url, trailing } = splitUrlTrailingPunctuation(rawUrl);
    if (!url) return rawUrl;
    const href = /^https?:\/\//i.test(url) ? url : `https://${url}`;
    const token = `\uE000${links.length}\uE001`;
    links.push({ token, href, display: compactDisplayUrl(href) });
    return `${token}${trailing}`;
  });
  return { text, links };
}

function removePromotedUrlToken(text, token) {
  const index = text.indexOf(token);
  if (index < 0) return text;

  let before = text.slice(0, index);
  let after = text.slice(index + token.length);
  if (!before) after = after.replace(/^\s+/, '');
  else if (!after) before = before.replace(/\s+$/, '');
  else if (/\s$/.test(before) && /^\s/.test(after)) after = after.replace(/^\s/, '');
  else if (/\s$/.test(before) && /^[.,!?;:\)\]\}]/.test(after)) before = before.replace(/\s+$/, '');
  return `${before}${after}`;
}

/** Prepare message text without coupling URL parsing to the HTML renderer. */
export function prepareMessageUrlPresentation(value, { promoteFirstUrl = false } = {}) {
  const tokenized = tokenizeMessageUrls(value);
  if (!promoteFirstUrl || tokenized.links.length === 0) {
    return { ...tokenized, promoted: null };
  }

  const [promoted, ...links] = tokenized.links;
  return {
    text: removePromotedUrlToken(tokenized.text, promoted.token),
    links,
    promoted
  };
}

function normalizedFullUrl(value) {
  const parsed = normalizedUrl(value);
  return parsed?.href || '';
}

function embeddedPreviewCandidates(previews) {
  if (Array.isArray(previews)) {
    return previews.filter(preview => preview && typeof preview === 'object');
  }
  if (!previews || typeof previews !== 'object') return [];
  return Object.entries(previews).flatMap(([url, preview]) => {
    if (!preview || typeof preview !== 'object' || Array.isArray(preview)) return [];
    return [{ ...preview, url: preview.url || url }];
  });
}

/** Match only backend-provided embedded URL metadata. Exact URL matches win;
 * a same-page canonical fallback is accepted only when it is unambiguous.
 * The current API returns inMessageUrls as an object keyed by the cleaned URL;
 * older responses may still contain the previous array representation. */
export function findEmbeddedUrlPreview(href, previews) {
  const candidates = embeddedPreviewCandidates(previews);
  if (candidates.length === 0) return null;
  const exactHref = normalizedFullUrl(href);
  if (!exactHref) return null;

  const exact = candidates.filter(preview => [preview.url, preview.canonicalUrl]
    .some(value => value && normalizedFullUrl(value) === exactHref));
  if (exact.length === 1) return exact[0];
  if (exact.length > 1) return null;

  const samePage = candidates.filter(preview => [preview.url, preview.canonicalUrl]
    .some(value => value && urlsReferenceSamePage(value, href)));
  return samePage.length === 1 ? samePage[0] : null;
}

/** Social-card metadata belongs to the message page. Only reuse it for an
 * embedded link when both URLs identify that same page. */
export function urlsReferenceSamePage(left, right) {
  const a = normalizedUrl(left);
  const b = normalizedUrl(right);
  if (!a || !b) return false;
  const cleanHost = url => url.hostname.toLowerCase().replace(/^www\./, '');
  const cleanPath = url => {
    const value = url.pathname.replace(/\/+$/, '');
    return value || '/';
  };
  return cleanHost(a) === cleanHost(b)
    && cleanPath(a) === cleanPath(b)
    && a.port === b.port;
}
